const PAGINATION_COMMENT_RE = /<!--\s*handout-pagination:([\s\S]*?)\s*-->/gi

export const HANDOUT_PAGE_GAP = 34

export function calcHandoutPageHeight(contentWidth) {
  const width = Number(contentWidth) || 760
  return Math.max(720, Math.min(980, Math.round(width * 1.25)))
}

export function stripHandoutPaginationComment(html) {
  return String(html || '').replace(PAGINATION_COMMENT_RE, '').trim()
}

export function extractHandoutPaginationPlan(html) {
  const source = String(html || '')
  const match = /<!--\s*handout-pagination:([\s\S]*?)\s*-->/i.exec(source)
  if (!match) return null
  try {
    const plan = JSON.parse(match[1])
    if (!plan || typeof plan !== 'object') return null
    return plan
  } catch (e) {
    return null
  }
}

export function embedHandoutPaginationComment(html, plan) {
  const clean = stripHandoutPaginationComment(html)
  if (!plan) return clean
  const payload = JSON.stringify(plan)
  return clean ? `${clean}\n<!--handout-pagination:${payload}-->` : `<!--handout-pagination:${payload}-->`
}

export function mergeIncomingHandoutPlan(prevPlan, incomingPlan) {
  if (!prevPlan) return incomingPlan || null
  if (!incomingPlan) return prevPlan.optionLayouts ? prevPlan : null
  const prevLayouts = prevPlan.optionLayouts
  const incomingLayouts = incomingPlan.optionLayouts
  if (!prevLayouts && !incomingLayouts) return incomingPlan
  const pickLayout = (a, b) => {
    const n = (v) => {
      const x = Number(v) || 1
      return [1, 2, 4].includes(x) ? x : 1
    }
    const p = a != null ? n(a) : null
    const d = b != null ? n(b) : null
    if (p == null) return d || 1
    if (d == null) return p
    if (p === d) return p
    if (p === 1) return d
    if (d === 1) return p
    return p
  }
  const mergeMaps = (prevMap, incomingMap) => {
    const merged = {}
    const keys = new Set([
      ...Object.keys(prevMap || {}),
      ...Object.keys(incomingMap || {})
    ])
    keys.forEach(key => {
      merged[key] = pickLayout(prevMap && prevMap[key], incomingMap && incomingMap[key])
    })
    return merged
  }
  const optionLayouts = {
    byBlockId: mergeMaps(prevLayouts && prevLayouts.byBlockId, incomingLayouts && incomingLayouts.byBlockId),
    byQuestionId: mergeMaps(prevLayouts && prevLayouts.byQuestionId, incomingLayouts && incomingLayouts.byQuestionId)
  }
  return { ...incomingPlan, optionLayouts }
}

export function encodePaginationPlanParam(contentHtml) {
  const plan = extractHandoutPaginationPlan(contentHtml || '')
  if (!plan) return ''
  try {
    const json = JSON.stringify(plan)
    const base64 = typeof btoa !== 'undefined'
      ? btoa(unescape(encodeURIComponent(json)))
      : Buffer.from(json, 'utf8').toString('base64')
    return encodeURIComponent(base64)
  } catch (e) {
    return ''
  }
}

function isPageBreakNode(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.matches('.handout-page-break-token,[data-handout-page-break="true"]')) return true
  const text = (node.textContent || '').replace(/\s+/g, '').trim()
  return text.includes('【分页符】') || text === '分页符'
}

function isQuestionMetaNode(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.classList && node.classList.contains('handout-inline-question')) return true
  if (node.hasAttribute('data-handout-question-block') && node.querySelector('[data-handout-question-source],[data-handout-question-difficulty]')) {
    return true
  }
  const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
  return /(单选题|多选题|填空题|问答题|选择题)/.test(text) && (/[★☆]{2,}/.test(text) || /[/／]/.test(text))
}

function isOptionNode(node) {
  if (!node || node.nodeType !== 1) return false
  if (node.hasAttribute('data-handout-question-block') && node.querySelector('strong,b')) {
    const st = (node.querySelector('strong,b').textContent || '').replace(/\s+/g, ' ').trim()
    if (/^[A-H][.、．]/i.test(st)) return true
  }
  const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
  if (/^[A-H][.、．]\s*/i.test(text)) return true
  const strong = node.querySelector('strong,b')
  if (strong) {
    const st = (strong.textContent || '').replace(/\s+/g, ' ').trim()
    if (/^[A-H][.、．]/i.test(st)) return true
  }
  return false
}

function getQuestionBlockId(node) {
  if (!node || node.nodeType !== 1) return ''
  const own = node.getAttribute('data-handout-question-block')
  if (own) return own
  const inner = node.querySelector('[data-handout-question-block]')
  return inner ? (inner.getAttribute('data-handout-question-block') || '') : ''
}

function getQuestionId(node) {
  if (!node || node.nodeType !== 1) return ''
  const own = node.getAttribute('data-question-id')
  if (own) return own
  const inner = node.querySelector('[data-question-id]')
  return inner ? (inner.getAttribute('data-question-id') || '') : ''
}

function isBlockLikeNode(node) {
  if (!node || node.nodeType !== 1) return false
  const tag = node.tagName
  if (/^(SCRIPT|STYLE)$/i.test(tag)) return false
  if (isPageBreakNode(node)) return true
  if (node.classList && node.classList.contains('table-container')) return true
  if (tag === 'TABLE') return true
  if (/^H[1-6]$/.test(tag) || tag === 'P' || tag === 'UL' || tag === 'OL' || tag === 'BLOCKQUOTE') return true
  if (getQuestionBlockId(node) || isQuestionMetaNode(node) || isOptionNode(node)) return true
  return false
}

function collectRootBlocks(root) {
  if (!root) return []
  const blocks = []
  const walk = (parent) => {
    Array.from(parent.children).forEach((node) => {
      if (node.nodeType !== 1) return
      if (/^(SCRIPT|STYLE)$/i.test(node.tagName)) return
      if (isBlockLikeNode(node)) {
        blocks.push(node)
        return
      }
      if (node.tagName === 'DIV' || node.tagName === 'SECTION') {
        walk(node)
        return
      }
      blocks.push(node)
    })
  }
  walk(root)
  return blocks
}

function collectQuestionGroup(blocks, startIndex) {
  const first = blocks[startIndex]
  const blockId = getQuestionBlockId(first)
  const questionId = getQuestionId(first)
  const group = [first]
  let index = startIndex + 1
  while (index < blocks.length) {
    const node = blocks[index]
    if (isPageBreakNode(node) || isQuestionMetaNode(node)) break
    const nodeBlockId = getQuestionBlockId(node)
    const nodeQuestionId = getQuestionId(node)
    if (blockId && nodeBlockId && nodeBlockId !== blockId) break
    if (!blockId && questionId && nodeQuestionId && nodeQuestionId !== questionId) break
    if (!blockId && !questionId && !nodeBlockId && !nodeQuestionId && !isOptionNode(node) && group.length > 1) break
    group.push(node)
    index += 1
  }
  return { group, nextIndex: index }
}

function collectLayoutUnits(root) {
  const blocks = collectRootBlocks(root)
  const units = []
  let index = 0
  while (index < blocks.length) {
    const node = blocks[index]
    if (isPageBreakNode(node)) {
      units.push({ nodes: [node], manualBreak: true })
      index += 1
      continue
    }
    if (isQuestionMetaNode(node)) {
      const { group, nextIndex } = collectQuestionGroup(blocks, index)
      units.push({ nodes: group, manualBreak: false, question: true })
      index = nextIndex
      continue
    }
    units.push({ nodes: [node], manualBreak: false, question: false })
    index += 1
  }
  return units
}

function measureOffsetTop(node, ancestor) {
  if (!node || !ancestor) return 0
  let el = node
  let top = 0
  while (el && el !== ancestor) {
    top += el.offsetTop || 0
    el = el.offsetParent
    if (el && !ancestor.contains(el)) {
      const a = ancestor.getBoundingClientRect()
      const n = node.getBoundingClientRect()
      return (n.top - a.top) + (ancestor.scrollTop || 0)
    }
  }
  return top
}

export function measureNodeOffsetTop(node, ancestor) {
  return measureOffsetTop(node, ancestor)
}

function measureOffsetBottom(node, ancestor) {
  return measureOffsetTop(node, ancestor) + (node.offsetHeight || 0)
}

function measureUnit(unit, root) {
  if (!root || !unit.nodes.length) {
    return { top: 0, bottom: 0, height: 0 }
  }
  const tops = unit.nodes.map((node) => measureOffsetTop(node, root))
  const bottoms = unit.nodes.map((node) => measureOffsetBottom(node, root))
  const top = Math.min(...tops)
  const bottom = Math.max(...bottoms)
  return { top, bottom, height: Math.max(0, bottom - top) }
}

function measureSurfaceTopInCapture(surface, captureEl) {
  return Math.round(measureOffsetTop(surface, captureEl))
}

export function computeHandoutPaginationPlan(root, options = {}) {
  if (!root) return null
  const contentWidth = Math.round(root.clientWidth || 760)
  const pageHeight = calcHandoutPageHeight(contentWidth)
  const pageGap = HANDOUT_PAGE_GAP
  const units = collectLayoutUnits(root)
  if (!units.length) {
    return {
      version: 1,
      pageHeight,
      pageGap,
      contentWidth,
      breakBeforeIndexes: [],
      pageCount: 1,
      titleOffsetPx: Math.max(0, Number(options.titleOffsetPx) || 0)
    }
  }

  const getPageIndex = (virtualTop) => Math.floor(Math.max(0, virtualTop) / pageHeight)

  let consumedPageGaps = 0
  const breakBeforeIndexes = []
  let previousEndPage = 0

  units.forEach((unit, index) => {
    if (unit.manualBreak) {
      consumedPageGaps += pageGap
      const measure = measureUnit(unit, root)
      previousEndPage = getPageIndex(Math.max(0, measure.bottom - consumedPageGaps - 1))
      return
    }

    const { top, bottom } = measureUnit(unit, root)
    const virtualTop = Math.max(0, top - consumedPageGaps)
    const startPage = getPageIndex(virtualTop)
    const endPage = getPageIndex(Math.max(virtualTop, bottom - consumedPageGaps - 1))

    if (index > 0 && startPage > previousEndPage && !units[index - 1].manualBreak) {
      breakBeforeIndexes.push(index)
    }

    previousEndPage = endPage
  })

  const lastUnit = units[units.length - 1]
  const lastMeasure = measureUnit(lastUnit, root)
  const totalHeight = Math.max(pageHeight, lastMeasure.bottom - consumedPageGaps)
  const pageCount = Math.max(1, Math.ceil(totalHeight / pageHeight))

  return {
    version: 1,
    pageHeight,
    pageGap,
    contentWidth,
    breakBeforeIndexes,
    pageCount,
    titleOffsetPx: Math.max(0, Number(options.titleOffsetPx) || 0)
  }
}

export function applyHandoutPageBackground(root, plan) {
  if (!root || !plan) return
  const pageHeight = plan.pageHeight || calcHandoutPageHeight(plan.contentWidth)
  const pageCount = Math.max(1, Number(plan.pageCount) || 1)
  root.style.minHeight = `${pageCount * pageHeight}px`
}

/** 预览页码：优先用 layout 后的 minHeight，避免分页符撑开前少算页数 */
export function resolveHandoutPreviewPageCount(surface, pageHeight, planCount = 1) {
  if (!surface || pageHeight <= 0) return Math.max(1, Number(planCount) || 1)
  let minHeight = 0
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    minHeight = parseFloat(window.getComputedStyle(surface).minHeight) || 0
  }
  const surfaceHeight = Math.max(
    surface.scrollHeight || 0,
    surface.offsetHeight || 0,
    minHeight
  )
  const measured = Math.max(1, Math.ceil(surfaceHeight / pageHeight))
  return Math.max(measured, Math.max(1, Number(planCount) || 1))
}

export function resolveHandoutPreviewPageHeight(surface, plan) {
  if (!surface) return Math.max(1, Number(plan && plan.pageHeight) || 760)
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    const fromVar = parseFloat(window.getComputedStyle(surface).getPropertyValue('--handout-page-height'))
    if (Number.isFinite(fromVar) && fromVar > 0) return fromVar
  }
  const width = surface.clientWidth || (plan && plan.contentWidth) || 760
  return Math.max(1, Number(plan && plan.pageHeight) || calcHandoutPageHeight(width))
}

export function buildHandoutPreviewPageNumberPositions(surface, scrollEl, plan) {
  if (!surface || !scrollEl || !plan) return []
  const pageHeight = resolveHandoutPreviewPageHeight(surface, plan)
  const pageCount = resolveHandoutPreviewPageCount(surface, pageHeight, plan.pageCount)
  const pageGap = Number(plan.pageGap) || HANDOUT_PAGE_GAP
  const surfaceTop = measureNodeOffsetTop(surface, scrollEl)
  const bottomInset = Math.max(14, Math.round(pageGap * 0.5) + 8)
  const positions = []
  for (let page = 1; page <= pageCount; page += 1) {
    positions.push({
      page,
      top: surfaceTop + page * pageHeight - bottomInset
    })
  }
  return positions
}

export function resolveHandoutBodySurface(captureEl) {
  if (!captureEl) return null
  return captureEl.querySelector('[data-slate-editor].handout-body-surface')
    || captureEl.querySelector('[data-slate-editor]')
    || captureEl.querySelector('.handout-body-surface')
}

export function resolveHandoutTitleOffsetPx(captureEl) {
  if (!captureEl) return 0
  const richEditor = captureEl.closest('.rich-editor')
  if (!richEditor) return 0
  const banner = richEditor.querySelector('.handout-title-banner')
  return banner ? Math.round(banner.offsetHeight || 0) : 0
}

/** 导出时优先用当前预览 DOM 的分页，避免与编辑器正文不一致 */
export function resolveLiveHandoutPaginationPlan(captureEl, fallbackHtml) {
  const surface = resolveHandoutBodySurface(captureEl)
  if (surface) {
    const titleOffsetPx = resolveHandoutTitleOffsetPx(captureEl)
    const plan = computeHandoutPaginationPlan(surface, { titleOffsetPx })
    if (plan) return plan
  }
  const embedded = extractHandoutPaginationPlan(fallbackHtml || '')
  if (embedded) return embedded
  const fallbackSurface = resolveHandoutBodySurface(captureEl)
  const contentWidth = fallbackSurface ? Math.round(fallbackSurface.clientWidth || 760) : 760
  const pageHeight = calcHandoutPageHeight(contentWidth)
  return {
    version: 1,
    pageHeight,
    pageGap: HANDOUT_PAGE_GAP,
    contentWidth,
    breakBeforeIndexes: [],
    pageCount: 1,
    titleOffsetPx: resolveHandoutTitleOffsetPx(captureEl)
  }
}

/** 导出前把放不下的整题推到下一页（与预览分页符效果一致） */
export function applyHandoutExportKeepTogether(surface, plan) {
  if (!surface || !plan) return () => {}
  const pageHeight = Math.max(1, Number(plan.pageHeight) || calcHandoutPageHeight(surface.clientWidth))
  const pageGap = plan.pageGap || HANDOUT_PAGE_GAP
  const units = collectLayoutUnits(surface)
  const restored = []
  let consumedGaps = 0

  units.forEach((unit) => {
    if (unit.manualBreak) {
      consumedGaps += pageGap
      return
    }
    if (!unit.question || !unit.nodes.length) return

    const { top, height } = measureUnit(unit, surface)
    if (height > pageHeight) return

    const virtualTop = Math.max(0, top - consumedGaps)
    const offsetInPage = virtualTop % pageHeight
    const spaceLeft = pageHeight - offsetInPage

    if (offsetInPage > 2 && height > spaceLeft + 2) {
      const first = unit.nodes[0]
      const prevMargin = first.style.marginTop || ''
      const base = parseFloat(prevMargin) || 0
      first.style.marginTop = `${base + spaceLeft}px`
      first.setAttribute('data-handout-export-pad', String(spaceLeft))
      restored.push({ node: first, prevMargin })
    }
  })

  return () => {
    restored.forEach(({ node, prevMargin }) => {
      node.style.marginTop = prevMargin
      node.removeAttribute('data-handout-export-pad')
    })
  }
}

/** 按 sliceRoot 顶边切片；页数与预览 resolveHandoutPreviewPageCount 一致 */
export function computeHandoutPageSliceStarts(captureEl, plan, options = {}) {
  const surface = resolveHandoutBodySurface(captureEl)
  if (!surface || !plan) return null
  const sliceRoot = (options && options.sliceRoot) || captureEl
  const pageHeight = resolveHandoutPreviewPageHeight(surface, plan)
  const pageCount = resolveHandoutPreviewPageCount(surface, pageHeight, plan.pageCount)
  const starts = []
  for (let i = 0; i < pageCount; i++) {
    starts.push(i * pageHeight)
  }
  return {
    starts,
    pageHeight,
    totalHeight: pageCount * pageHeight,
    surfaceTop: sliceRoot === surface ? 0 : measureSurfaceTopInCapture(surface, captureEl),
    pageCount
  }
}

export function layoutHandoutPageBreaks(root, plan) {
  if (!root) return
  const pageHeight = plan && plan.pageHeight > 0
    ? plan.pageHeight
    : calcHandoutPageHeight(root.clientWidth || 760)
  const pageGap = plan && plan.pageGap > 0 ? plan.pageGap : HANDOUT_PAGE_GAP
  root.style.setProperty('--handout-page-height', `${pageHeight}px`)
  root.style.setProperty('--handout-page-gap', `${pageGap}px`)
  let consumedPageGaps = 0
  Array.from(root.querySelectorAll('.handout-page-break-token')).forEach(node => {
    const contentTop = Math.max(0, (node.offsetTop || 0) - consumedPageGaps)
    const offsetInPage = contentTop % pageHeight
    const remaining = Math.max(120, pageHeight - offsetInPage)
    const breakHeight = remaining + pageGap
    node.style.setProperty('--handout-page-break-height', `${breakHeight}px`)
    node.style.setProperty('--handout-page-break-fill-height', `${remaining}px`)
    consumedPageGaps += pageGap
  })
  applyHandoutPageBackground(root, plan || { pageHeight, pageCount: 1 })
}
